import { Box, ButtonBase, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";

import type { SxStyle } from "../heatStyles.js";

/**
 * Shared layout + interaction primitives for the lead detail page. Extracted
 * verbatim from `LeadPage.tsx` so each is independently testable and reusable
 * across the page's section cards.
 */

/** Card surface (was the global `.panel`). */
export const panelSx: SxStyle = {
  display: "grid",
  gap: 1,
  borderRadius: 1,
  border: 1,
  borderColor: "divider",
  bgcolor: "background.paper",
  boxShadow: (t: Theme) => t.shadows[1],
  p: 2.5,
};

/** Recessed sub-box (was the global `.panel-inset`). */
export const panelInsetSx: SxStyle = {
  borderRadius: 1,
  border: 1,
  borderColor: "divider",
  bgcolor: "surfaceSoft",
};

export function Section({
  title, icon, action, tone = "default", children,
}: {
  title: string;
  icon?: string;
  action?: ReactNode;
  tone?: "default" | "good";
  children: ReactNode;
}): JSX.Element {
  return (
    <Box
      component="section"
      sx={[
        panelSx,
        { gap: 1, p: 2.5 },
        tone === "good"
          ? { borderColor: (t: Theme) => alpha(t.palette.success.main, 0.45) }
          : null,
      ]}
    >
      <Box component="header" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography component="h3" sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
          {icon ? <Box component="span" aria-hidden>{icon}</Box> : null}
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

export function Collapsible({
  title, icon, meta, defaultOpen = true, children,
}: {
  title: string;
  icon?: string;
  meta?: string | null;
  defaultOpen?: boolean;
  children: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box
      component="section"
      data-print-expand="1"
      sx={[panelSx, { gap: 1, p: 2.5 }]}
    >
      <ButtonBase
        className="no-print"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, textAlign: "left" }}
      >
        <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
          {icon ? <Box component="span" aria-hidden>{icon}</Box> : null}
          {title}
        </Box>
        <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 11, color: "text.disabled" }}>
          {meta ? <span>{meta}</span> : null}
          <Box component="span" aria-hidden>{open ? "▾" : "▸"}</Box>
        </Box>
      </ButtonBase>
      {/* Print-only fixed header so the printed copy still labels the section */}
      <Box component="header" className="print-only" sx={{ display: "none" }}>
        <h3 style={{ margin: 0, fontSize: "0.85rem", fontWeight: 600 }}>
          {title}
        </h3>
      </Box>
      {/* `print-collapsed-show` is the print-stylesheet hook that forces a
       * collapsed body to render in the PDF; normal hiding is done by sx. */}
      <Box
        className={open ? undefined : "print-collapsed-show"}
        sx={open ? { display: "grid", gap: 1 } : { display: "none" }}
      >
        {children}
      </Box>
    </Box>
  );
}

/**
 * Clamp long prose to `collapsedHeight` with a fade + "Show more/less", so a
 * wall of agent-written text stays scannable WITHOUT hiding it behind a
 * collapse. The toggle only appears when the content actually overflows
 * (measured), and print always renders the full content. In jsdom (unit tests)
 * scrollHeight is 0, so it renders un-clamped — content is always present.
 */
export function ExpandableText({
  children, collapsedHeight = 140,
}: { children: ReactNode; collapsedHeight?: number }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) setOverflows(el.scrollHeight > collapsedHeight + 4);
  }, [collapsedHeight, children]);
  const clamp = !open && overflows;
  return (
    <Box sx={{ display: "grid", gap: 0.5 }}>
      <Box
        ref={ref}
        // `expandable-clamp` is the hook PrintStyles `.capturing-pdf` uses to
        // un-clamp this prose for the PDF snapshot — without it the capture
        // would freeze the on-screen "Show more" truncation into the export.
        className="expandable-clamp"
        sx={{
          maxHeight: open ? "none" : collapsedHeight,
          overflow: "hidden",
          ...(clamp
            ? {
                WebkitMaskImage: "linear-gradient(to bottom, #000 64%, transparent)",
                maskImage: "linear-gradient(to bottom, #000 64%, transparent)",
              }
            : {}),
          "@media print": { maxHeight: "none", WebkitMaskImage: "none", maskImage: "none" },
        }}
      >
        {children}
      </Box>
      {overflows ? (
        <ButtonBase
          className="no-print"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          sx={{ justifySelf: "start", fontSize: 12, fontWeight: 600, color: "primary.main", py: 0.25 }}
        >
          {open ? "Show less ▴" : "Show more ▾"}
        </ButtonBase>
      ) : null}
    </Box>
  );
}

export function CopyButton({
  text, label,
}: { text: string; label: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard unavailable — silently no-op */
    }
  };
  return (
    <ButtonBase
      onClick={onCopy}
      aria-label={label}
      title={label}
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.default",
        px: 1,
        py: 0.25,
        fontSize: 11,
        fontWeight: 500,
        color: "text.primary",
        "&:hover": { bgcolor: "surfaceSoft" },
      }}
    >
      {copied ? "✓ copied" : "Copy"}
    </ButtonBase>
  );
}
